import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { ValidatedAddressModel } from "@insite/client-framework/Types/ApiModels";
import Button from "@insite/mobius/Button";
import Icon from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import XCircle from "@insite/mobius/Icons/XCircle";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import themeColors, { themeFontSizes, themeFontWeights } from "@MasterBluePrint/Styles/variables";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    isOpen: boolean;
    /** True → Mode A (suggested vs entered). False → Mode B (no suggestion / technical error). */
    hasSuggestion: boolean;
    enteredAddress?: ValidatedAddressModel;
    suggestedAddress?: ValidatedAddressModel;
    /** Mode A primary — apply Piyovi's standardized address. */
    onUseSuggested: () => void;
    /** Mode A secondary / Mode B primary — keep the address as entered. */
    onKeepEntered: () => void;
    /** Mode B secondary + modal close — return to the form to edit. */
    onEdit: () => void;
    extendedStyles?: PiyoviAddressConfirmModalStyles;
}

export interface PiyoviAddressConfirmModalStyles {
    modal?: ModalPresentationProps;
    container?: InjectableCss;
    secondaryButton?: InjectableCss;
    primaryButton?: InjectableCss;
}

export const piyoviAddressConfirmModalStyles: PiyoviAddressConfirmModalStyles = {
    modal: {
        sizeVariant: "small",
    },
    container: {
        css: css`
        margin-left:20px;
        .closeAddressFormErrorsContainer{
           position: absolute;
           right: 1.25rem;
           top: 24px;
           .close-label {
                color: #535E64;
                font-family: Roboto;
                font-size: .875rem;
                letter-spacing: 0.16px;
                line-height: 1;
                display: inline-block;
                margin-right: .625rem;
                vertical-align: middle;
           }
           .icon-close {
                display: inline-block;
                cursor: pointer;
                vertical-align: middle;
                line-height: 1;
                width: 1.125rem;
                height: 1.125rem;
                color: #535E64;
                margin-top:-1px;
                svg {
                    height: 1.125rem;
                    width: 1.125rem;
                }
            }
        }
        .addressFormErrorsModalHeading {
            margin-top: 2.1875rem;
            margin-bottom: 0;
            margin-left: 0;
            color: ${themeColors.colorPrimary};
            font-family: 'Roboto', sans-serif;
            font-size: ${themeFontSizes.size20};
            font-weight: ${themeFontWeights.fontWeightMedium};
            line-height: 1.5625rem;
            position: relative;
            letter-spacing: 0;

            &:after {
                height: 0.188rem;
                width: 2.50rem;
                background-color:  ${themeColors.colorRed};
                content: "";
                display: inline-block;
                position: absolute;
                bottom: -0.4rem;
                left: 0.0625rem;
            }
        }
        .addressFormErrorMessageParentDiv{
            width:"100%";
            .addressFormErrorMessageChild1Div{
                float: left;
                width: 8%;
                .icon-warning{
                    color: ${themeColors.colorDanger};
                }
            }
            .addressFormErrorMessageChild2Div{
                width: 92%;
                .addressFormErrorMessage{
                    margin-top: 2.1875rem;
                    margin-bottom: 0;
                    color: ${themeColors.colorDanger};
                    font-family: 'Roboto', sans-serif;
                    font-size: ${themeFontSizes.size14};
                    font-weight: ${themeFontWeights.fontWeightBold};
                    line-height: 1.5625rem;
                    position: relative;
                    margin-left: 0;
                }
                .addressFormErrorHelperText{
                    margin-bottom: 1.5625rem;
                    color: ${themeColors.colorDanger};
                    font-family: 'Roboto', sans-serif;
                    font-style: italic;
                    font-size: ${themeFontSizes.size14};
                    line-height: 1.5625rem;
                    position: relative;
                    margin-left: 0;
                }
            }
        }
        .byPassAddressValidationHLine{
            width:"100%",
            background-color:"#003366";
            box-sizing: border-box;
            height: 1px;
            border: 1px solid #D7D7D7;
            opacity: 0.72;
            margin-bottom: 1.5625rem;
        }
        .piyoviModalBody{
            margin: 1.25rem 0;
            color: ${themeColors.colorAddress};
            font-family: 'Roboto', sans-serif;
            font-size: ${themeFontSizes.size14};
            line-height: 1.25rem;
        }
        .piyoviOptionLabel{
            display: block;
            font-weight: ${themeFontWeights.fontWeightBold};
            color: ${themeColors.colorPrimary};
        }
        .piyoviOptionAddress{
            color: ${themeColors.colorAddress};
            font-family: 'Roboto', sans-serif;
            font-size: ${themeFontSizes.size14};
            line-height: 1.25rem;
        }
        .piyoviOptionAddressBlock{
            margin-bottom: 1.25rem;
            .piyoviOptionAddress{
                margin-left: 0;
            }
        }
        .piyoviModalActions{
            display: flex;
            justify-content: space-between;
            gap: 0.9375rem;
            margin-top: 1rem;
        }
        `,
    },
    secondaryButton: {
        css: css`
            text-transform: capitalize;
            text-decoration: underline;
            flex: 1;
            cursor: pointer;
            height: 44px;
            line-height: 1;
            transition: all 200ms ease-in-out;
            border-radius: 0.125rem;
            padding: 0 21px;
            border: 2px solid #002554;
            background: #fff;
            color: #002554;
            font-family: Roboto;
            font-size: 0.875rem;
            text-align: center;
            span {
                height: 44px;
                line-height: 44px;
                color: #002554 !important;
                vertical-align: baseline;
            }
            &:hover,
            &:active,
            &:focus {
                border-color: #002554;
                border-radius: 0.125rem;
                background-color: #2060ad;
                color: #fff !important;
                span {
                    color: #fff !important;
                }
            }
        `,
    },
    primaryButton: {
        css: css`
            text-transform: uppercase;
            height: 44px;
            font-size: 0.875rem;
            flex: 1;
            text-align: center;
            span {
                font-size: 0.875rem;
            }
        `,
    },
};

const formatAddressLines = (address?: ValidatedAddressModel) => {
    if (!address) {
        return [];
    }
    const line1 = [address.address1, address.address2].filter(Boolean).join(", ");
    const line2 = [
        [address.city, address.stateProvince].filter(Boolean).join(", "),
        address.zipPostalCode,
    ]
        .filter(Boolean)
        .join(" ");
    return [line1, line2].filter(Boolean);
};

/**
 * Presentational Piyovi address-confirmation modal (suggested vs entered).
 * Extracted from `CheckoutShippingAddresses.tsx` so it can be reused by the
 * "Add New Address" flow. Pure UI — the caller owns all state and actions.
 */
const PiyoviAddressConfirmModal: FC<OwnProps> = ({
    isOpen,
    hasSuggestion,
    enteredAddress,
    suggestedAddress,
    onUseSuggested,
    onKeepEntered,
    onEdit,
    extendedStyles,
}) => {
    const [styles] = useState(() => mergeToNew(piyoviAddressConfirmModalStyles, extendedStyles));

    const piyoviAddressMessage = siteMessage("Piyovi_Address_Message").toString();
    const piyoviAddressMessageHelpText = siteMessage("Piyovi_Address_Message_Help_Text").toString();

    const showSuggestion = hasSuggestion && !!suggestedAddress;
    const suggestedLines = formatAddressLines(suggestedAddress);
    const enteredLines = formatAddressLines(enteredAddress);

    return (
        <Modal
            isOpen={isOpen}
            data-test-selector="piyoviAddressConfirmModal"
            {...styles.modal}
            handleClose={onEdit}
            className="confirm-remove-modal"
        >
            <Typography as="div" {...styles.container}>
                <Typography as="div" onClick={onEdit} className="closeAddressFormErrorsContainer">
                    <span className="close-label">{translate("Close")}</span>
                    <Icon className="icon-close" src={XCircle} />
                </Typography>
                <Typography as="div">
                    <Typography className="addressFormErrorsModalHeading" as="h1">
                        {translate("Verify your address")}
                    </Typography>
                </Typography>

                {showSuggestion ? (
                    <>
                        <Typography as="p" className="piyoviModalBody">
                            {translate(
                                "We were unable to verify the address you entered, which may result in delivery delays. Please confirm your address below.",
                            )}
                        </Typography>
                        <Typography as="div" className="piyoviOptionAddressBlock" data-test-selector="piyoviSuggestedOption">
                            <Typography as="span" className="piyoviOptionLabel">
                                {translate("Suggested address (verified)")}
                            </Typography>
                            {suggestedLines.map((line, i) => (
                                <Typography as="div" key={`sug-${i}`} className="piyoviOptionAddress">
                                    {line}
                                </Typography>
                            ))}
                        </Typography>
                        <Typography as="div" className="piyoviOptionAddressBlock" data-test-selector="piyoviEnteredOption">
                            <Typography as="span" className="piyoviOptionLabel">
                                {translate("You entered")}
                            </Typography>
                            {enteredLines.map((line, i) => (
                                <Typography as="div" key={`ent-${i}`} className="piyoviOptionAddress">
                                    {line}
                                </Typography>
                            ))}
                        </Typography>
                        <Typography as="hr" className="byPassAddressValidationHLine"></Typography>
                        <Typography as="div" className="piyoviModalActions">
                            <Button
                                {...styles.secondaryButton}
                                onClick={onKeepEntered}
                                data-test-selector="piyoviKeepMyAddressButton"
                            >
                                {translate("Keep my address")}
                            </Button>
                            <Button
                                {...styles.primaryButton}
                                onClick={onUseSuggested}
                                data-test-selector="piyoviUseSuggestedButton"
                            >
                                {translate("Use suggested")}
                            </Button>
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography as="div" className="addressFormErrorMessageParentDiv">
                            <Typography as="div" className="addressFormErrorMessageChild1Div">
                                <Icon className="icon-warning" src={AlertTriangle} />
                            </Typography>
                            <Typography as="div" className="addressFormErrorMessageChild2Div">
                                <Typography as="div">
                                    <Typography className="addressFormErrorMessage" as="h3">
                                        {piyoviAddressMessage ||
                                            translate(
                                                "We couldn't verify this address. It may be incomplete or incorrect and could result in delivery delays.",
                                            )}
                                    </Typography>
                                </Typography>
                                <Typography as="div">
                                    <Typography className="addressFormErrorHelperText" as="p">
                                        {piyoviAddressMessageHelpText ||
                                            translate("Please review your address carefully before continuing.")}
                                    </Typography>
                                </Typography>
                            </Typography>
                        </Typography>
                        <Typography as="div" className="piyoviOptionAddressBlock">
                            {enteredLines.map((line, i) => (
                                <Typography as="div" key={`ent-b-${i}`} className="piyoviOptionAddress">
                                    {line}
                                </Typography>
                            ))}
                        </Typography>
                        <Typography as="hr" className="byPassAddressValidationHLine"></Typography>
                        <Typography as="div" className="piyoviModalActions">
                            <Button
                                {...styles.secondaryButton}
                                onClick={onEdit}
                                data-test-selector="piyoviEditAddressButton"
                            >
                                {translate("Edit address")}
                            </Button>
                            <Button
                                {...styles.primaryButton}
                                onClick={onKeepEntered}
                                data-test-selector="piyoviContinueWithAddressButton"
                            >
                                {translate("Continue with this address")}
                            </Button>
                        </Typography>
                    </>
                )}
            </Typography>
        </Modal>
    );
};

export default PiyoviAddressConfirmModal;
